import { inject } from 'aurelia';
import { IHttpClient } from '@aurelia/fetch-client';
import { ApiBase } from '../api-base';

export interface MyRegistrationTeam {
    id: string;
    suffix: string;
    // Client-only UI state for duplicate validation, not part of the API response.
    isDuplicate?: boolean;
}

export interface MyRegistrationMember {
    id: string;
    email: string;
    role: 'Manager' | 'Contact';
    isAccepted: boolean;
    isSelf: boolean;
}

export interface MyRegistration {
    associationId: string;
    associationName: string;
    groupId: string;
    myRole: 'Manager' | 'Contact';
    isAccepted: boolean;
    teams: MyRegistrationTeam[];
    members: MyRegistrationMember[];
    // Client-only UI state for the manage view, not part of the API response.
    newMemberEmail?: string;
    newMemberRole?: 'Manager' | 'Contact';
    hasDuplicateTeamSuffixes?: boolean;
}

@inject(IHttpClient)
export class AssociationApi extends ApiBase {
    constructor(http: IHttpClient) {
        super(http);
    }

    public async getMyRegistrations(eventCode: string): Promise<MyRegistration[]> {
        const r = await this.get(`api/events/${eventCode}/registration/mine`);
        return (r.content as MyRegistration[]) ?? [];
    }

    public async rename(associationId: string, name: string): Promise<void> {
        await this.patch(`api/associations/${associationId}`, { name });
    }

    public async addTeam(associationId: string, suffix: string): Promise<MyRegistrationTeam> {
        const r = await this.post(`api/associations/${associationId}/teams`, { suffix });
        return r.content as MyRegistrationTeam;
    }

    public async removeTeam(associationId: string, teamSettingId: string): Promise<void> {
        await this.delete(`api/associations/${associationId}/teams/${teamSettingId}`);
    }

    public async renameTeam(associationId: string, teamSettingId: string, suffix: string): Promise<MyRegistrationTeam> {
        const r = await this.patch(`api/associations/${associationId}/teams/${teamSettingId}`, { suffix });
        return r.content as MyRegistrationTeam;
    }

    public async inviteMember(associationId: string, email: string, role: 'Manager' | 'Contact'): Promise<MyRegistrationMember> {
        const r = await this.post(`api/associations/${associationId}/members`, { email, role });
        return r.content as MyRegistrationMember;
    }

    public async removeMember(associationId: string, associationUserId: string): Promise<void> {
        await this.delete(`api/associations/${associationId}/members/${associationUserId}`);
    }

    public async leave(associationId: string): Promise<void> {
        await this.delete(`api/associations/${associationId}/members/self`);
    }

    public async acceptInvite(associationId: string): Promise<void> {
        await this.post(`api/associations/${associationId}/members/accept`, {});
    }

    public async revoke(associationId: string): Promise<void> {
        await this.delete(`api/associations/${associationId}`);
    }
}
